'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import Link, {} from 'next/link.js';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
const filterObjectByKeys = (objectToFilter, queriesToKeep) => {
    return Object.keys(objectToFilter)
        .filter((key) => queriesToKeep.includes(key))
        .reduce((obj, key) => {
        obj[key] = objectToFilter[key];
        return obj;
    }, {});
};
/**
 * Combines query strings (in our QueryStringSchema format) into one, keeping only queries that are specifiedc in the queriesToKeep list.
 * Optionally all queries could also be kept by passing 'ALL' string.
 *
 * @param newSearchParams - the new search params to add, given in our QueryStringSchema format
 * @param currentSearchParams - the URLSearchParams that are already present in our url
 * @param queriesToKeep - list to specify which queries to keep
 * @returns the final combined query
 */
export function combineQueryParams(newSearchParams, currentSearchParams, queriesToKeep) {
    const currentSearchParamsObj = Object.fromEntries(currentSearchParams.entries());
    // TODO: import a list of "persisted params" that should always be persisted, and just append it to the allowedKeys/queriesToKeep array
    if (queriesToKeep === 'ALL') {
        return { ...currentSearchParamsObj, ...newSearchParams };
    }
    const filteredParams = filterObjectByKeys({ ...currentSearchParamsObj }, [...queriesToKeep]);
    const combined = { ...filteredParams, ...newSearchParams };
    return combined;
}
/**
 * A next/link wrapper for keeping querystring parameters and making sure they are strictly typed to our schema.
 *
 * @param root0 the input props
 * @param root0.className - classname for the Link component
 * @param root0.href object with pathname, query and hash to send to the link component, the query is typed according to the QueryStringSchema to always be a correct querystring.
 * @param root0.queriesToKeep the queries that should be kept when navigating with this link
 * @param root0.children the children to pass
 * @param root0.scroll whether Next.js should scroll to the target after navigation, defaults to true
 * @returns A Nextjs Link that keeps the queryparams we define.
 * @example
 *  &lt;TipsenLink
 *      href={{
 *        query: { event: 1 },
 *      }}
 *      queriesToKeep={['draw', 'drawOpen']}
 *   >
 *      TipsenLink
 *  &lt;/TipsenLink>
 */
function TipsenLink({ className, href, queriesToKeep, children, scroll: shouldScroll = true, ...props }) {
    const currentSearchParams = useSearchParams();
    const basePath = process.env['NEXT_PUBLIC_BASE_PATH'] ?? '';
    const fullHref = useMemo(() => {
        const getFullHref = (customHref, query) => {
            return {
                ...customHref,
                query,
                pathname: customHref.pathname === undefined ? undefined : `${basePath}${customHref.pathname}`,
            };
        };
        return getFullHref(href, combineQueryParams(href.query ?? {}, currentSearchParams, queriesToKeep ?? []));
    }, [basePath, currentSearchParams, href, queriesToKeep]);
    return (_jsx(Link, { prefetch: false, className: className, href: fullHref, scroll: shouldScroll, ...props, children: children }));
}
export default TipsenLink;
//# sourceMappingURL=TipsenLink.js.map